/**
 * The site status log (tidaltech/koi#8261): dated, signed notes about a
 * site from Customer Success and Field Services. A CONTACT note records a
 * conversation with the site; the latest one is the site's last contact.
 */
export const siteNoteKinds = ['NOTE', 'CONTACT'] as const;
export type SiteNoteKind = (typeof siteNoteKinds)[number];

export interface SiteNote {
  id: string;
  customerId: string;
  siteId: string;
  kind: SiteNoteKind;
  text: string;
  author?: string | undefined;
  createTime?: string | undefined;
}

export interface SiteNoteInput {
  kind: SiteNoteKind;
  text: string;
}

export interface SiteNotePage {
  notes: SiteNote[];
  nextPageToken?: string | undefined;
}

// Matches the backend's limit in AddSiteNote.
export const MAX_SITE_NOTE_LENGTH = 2000;

export interface SiteStatusSummary {
  latest?: SiteNote | undefined;
  lastContact?: SiteNote | undefined;
}

/** Each site's latest note and latest contact, from ListSiteNotes(site "-"). */
export function summarizeSiteNotes(notes: readonly SiteNote[]): Map<string, SiteStatusSummary> {
  const out = new Map<string, SiteStatusSummary>();
  const newer = (a: SiteNote | undefined, b: SiteNote) =>
    !a || (b.createTime ?? '') > (a.createTime ?? '');
  for (const note of notes) {
    const summary = out.get(note.siteId) ?? {};
    if (newer(summary.latest, note)) summary.latest = note;
    if (note.kind === 'CONTACT' && newer(summary.lastContact, note)) summary.lastContact = note;
    out.set(note.siteId, summary);
  }
  return out;
}

/** "3 Oct" this year, "3 Oct 2025" otherwise. */
export function formatNoteDate(value: string | undefined, now = new Date()): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}
